import { createAsyncThunk } from "@reduxjs/toolkit";
import { AuthService } from "@root/services/auth";
import { LocalStorageService } from "@services/local-storage";
import { SessionStorageService } from "@services/session-storage";
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_SIGNUP,
  AUTH_FULL_SIGNUP,
  DELETE_ACCOUNT,
  RESET_PASS,
  SET_NEW_PASSWORD,
  VERIFY_RESET_PASS,
  CHANGE_PASSWORD_ACCOUNT,
  RESEND_SIGNUP_EMAIL,
  VERIFY_RESET_PASSWORD_OTP,
  VERIFY_SIGNUP_EMAIL_OTP,
} from "../action-types";
import { showAlert } from "../slices/features/alert";
import { getBaseUrl } from "../selectors/features";
import { TOAST_VARIANT } from "@root/constants/common";
import { t } from "i18next";
import { changeAppLanguage } from "@root/utils/change-language";
import { LANGUAGE, ROUTE_NAMES } from "@root/constants";
import { useToast } from "@chakra-ui/react";

const authService = new AuthService();
const localStorageService = new LocalStorageService();
const sessionStorageService = new SessionStorageService();

interface LoginPayload {
  payload: Record<string, string>;
  onSuccess: any;
  onError: any;
  isSocial: boolean;
}
interface VerifyOtpParams {
  email: string;
  otp: string;
  // Receives the verify response so the caller can show the organisation the
  // code actually belonged to, rather than the one cached at sign-up time.
  onSuccess?: (response?: any) => void;
  onError?: (msg: string) => void;
  navigate?: (path: string) => void;
}
export const login = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGIN,
  async (requestPayload: LoginPayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError, payload, isSocial } = requestPayload;
    const { data, error } = await authService.signIn(
      baseUrl,
      payload,
      isSocial
    );

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      const { accessToken, user } = data;
      const userRole = user?.role;

      if (userRole) {
        // Store token in localStorage with role key (shared across tabs)
        await localStorageService.persistToken(userRole, accessToken);
        // Set active role in sessionStorage (tab-specific)
        sessionStorageService.setCurrentRole(userRole);
      } else {
        // Fallback for backward compatibility
        await localStorageService.persist("authToken", accessToken);
      }

      authService.setCookies();
      accessToken && onSuccess(data);
    }

    // thunkAPI.dispatch(
    //   showAlert({
    //     message: t("common:profile.loginSucessFull"),
    //     type: TOAST_VARIANT.SUCCESS,
    //   })
    // );

    return thunkAPI.fulfillWithValue(data);
  }
);

export const loginAdmin = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGIN,
  async (requestPayload: LoginPayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError, payload, isSocial } = requestPayload;
    const { data, error } = await authService.signInAdmin(
      baseUrl,
      payload,
      isSocial
    );

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      const { accessToken, user } = data;
      const userRole = user?.role;

      if (userRole) {
        // Store token in localStorage with role key (shared across tabs)
        await localStorageService.persistToken(userRole, accessToken);
        // Set active role in sessionStorage (tab-specific)
        sessionStorageService.setCurrentRole(userRole);
      } else {
        // Fallback for backward compatibility
        await localStorageService.persist("authToken", accessToken);
      }

      authService.setCookies();
      accessToken && onSuccess(data);
    }

    // thunkAPI.dispatch(
    //   showAlert({
    //     message: t("common:profile.loginSucessFull"),
    //     type: TOAST_VARIANT.SUCCESS,
    //   })
    // );

    return thunkAPI.fulfillWithValue(data);
  }
);

export const logout = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGOUT,
  async (requestPayload: Record<string, string>, thunkAPI) => {
    // Get current role before clearing
    const currentRole = sessionStorageService.getCurrentRole();

    // Clear only current user's token from localStorage
    if (currentRole) {
      await localStorageService.removeToken(currentRole);
    }

    // Clear tab-specific data (sessionStorage)
    sessionStorageService.clear();

    const response = await authService.signOut();
    thunkAPI.fulfillWithValue({ payload: requestPayload });

    return response?.data;
  }
);

export const signup = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_SIGNUP,
  async (requestPayload: Record<any, any>, thunkAPI) => {
    const { payload, onError, onSuccess } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());

    const response = await authService.signUp(baseUrl, payload);

    if (response?.error) {
      onError(response);
      // 409 means an approval request is already queued; the caller asks the
      // user whether to replace it, so an error toast would just duplicate it.
      if (response?.statusCode !== 409) {
        thunkAPI.dispatch(
          showAlert({
            message:
              response?.data?.message ||
              response?.data?.userMessage ||
              response?.data?.code ||
              response?.data,
            type: TOAST_VARIANT.ERROR,
          })
        );
      }
    } else {
      onSuccess(response);
    }
  }
);
export const fullSignup = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_FULL_SIGNUP,
  async (requestPayload: Record<any, any>, thunkAPI) => {
    const { payload, token, onError, onSuccess } = requestPayload;

    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.fullSignUp(baseUrl, payload, token);

    if (response?.error) {
      onError(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.data?.message ||
            response?.data?.userMessage ||
            response?.data?.code ||
            response?.data,
          type: TOAST_VARIANT.ERROR,
        })
      );
    } else {
      onSuccess(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.statusMessage || t("common:profile.signupSuccessfull"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );
    }
  }
);

export const resetPass = createAsyncThunk<TObject, TObject, IActionOptions>(
  RESET_PASS,
  async (requestPayload: any, thunkAPI) => {
    const { payload, onSuccess, onError } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.resetPass(baseUrl, payload);

    if (response?.error) {
      thunkAPI.dispatch(
        showAlert({
          message: response?.data?.message || t("common:feedback.somethingWrong"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError();
      return thunkAPI.rejectWithValue({ payload: requestPayload });
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: response?.statusMessage,
          type: TOAST_VARIANT.SUCCESS,
        })
      );
      onSuccess();
      return thunkAPI.fulfillWithValue({ payload: requestPayload });
    }
  }
);

export const verifyResetPass = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(VERIFY_RESET_PASS, async (requestPayload: any, thunkAPI) => {
  const { payload, onSuccess } = requestPayload;
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const response = await authService.verifyResetPass(baseUrl, payload);

  if (response?.error) {
    return thunkAPI.rejectWithValue({ payload: requestPayload });
  } else {
    onSuccess();

    return thunkAPI.fulfillWithValue({ payload: requestPayload });
  }
});

export const setNewPassword = createAsyncThunk<
  TObject,
  {
    email: string;
    newPassword: string;
    confirmPassword: string;
    resetToken: string;
    onSuccess: any;
    onError: any;
  },
  IActionOptions
>(SET_NEW_PASSWORD, async (requestPayload, thunkAPI) => {
  const {
    email,
    newPassword,
    confirmPassword,
    resetToken,
    onSuccess,
    onError,
  } = requestPayload;
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const response = await authService.setNewPassword(baseUrl, {
    email,
    newPassword,
    confirmPassword,
    resetToken,
  });

  if (response?.error) {
    onError(response);
    return thunkAPI.rejectWithValue({ payload: requestPayload });
  } else {
    thunkAPI.dispatch(
      showAlert({
        message: response?.message || t("common:subscriptionSuccess.success"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );
    onSuccess(response);
    return thunkAPI.fulfillWithValue({ payload: requestPayload });
  }
});

export const deleteAccount = createAsyncThunk<TObject, TObject, IActionOptions>(
  DELETE_ACCOUNT,
  async (requestPayload: { onSuccess: any; onError: any }, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError } = requestPayload;
    const { data, error } = await authService.deleteAccount(baseUrl);

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: t("common:profile.deleteAccountSuccess"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );

      onSuccess(data);
      return thunkAPI.fulfillWithValue(data);
    }
  }
);

export const changePasswordAccount = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(
  CHANGE_PASSWORD_ACCOUNT,
  async (
    requestPayload: { onSuccess: any; onError: any; payload: any },
    thunkAPI
  ) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError, payload } = requestPayload;

    const { data, error } = await authService.changePasswordAccount(
      baseUrl,
      payload
    );
    console.log("error in thunk", error);
    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: t("common:profile.deleteAccountSuccess"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );

      onSuccess(data);
      return thunkAPI.fulfillWithValue(data);
    }
  }
);

export const resendEmail = createAsyncThunk<TObject, TObject, IActionOptions>(
  RESEND_SIGNUP_EMAIL,
  async (requestPayload: any, thunkAPI) => {
    const { payload, onSuccess, onError } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.resendEmail(baseUrl, payload);

    if (response?.error) {
      thunkAPI.dispatch(
        showAlert({
          message: response?.data || t("common:msgs.someThingWrong"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError();
      return thunkAPI.rejectWithValue({ payload: requestPayload });
    } else {
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.statusMessage ||
            t("common:emailVerification.resendSuccess"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );
      onSuccess();
      return thunkAPI.fulfillWithValue({ payload: requestPayload });
    }
  }
);


export const verifyResetPasswordOtpThunk = createAsyncThunk<
  TObject,
  VerifyOtpParams,
  IActionOptions
>(
  VERIFY_RESET_PASSWORD_OTP,
  async ({ email, otp, onSuccess, onError, navigate }, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());

    const response = await authService.verifyResetPasswordOtp(baseUrl, {
      email,
      otp,
    });

    const status = response?.status || response?.statusCode;
    const isSuccess = status === 200 || status === 201;

    if (!isSuccess || response?.error) {
      const message =
        response?.data?.message || t("common:emailVerification.invalidOtp");
      onError?.(message);

      thunkAPI.dispatch(
        showAlert({
          message,
          type: TOAST_VARIANT.ERROR,
        })
      );

      return thunkAPI.rejectWithValue({ message });
    }
    onSuccess?.();

    thunkAPI.dispatch(
      showAlert({
        message: response?.message || t("common:emailVerification.otpVerified"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );
    return thunkAPI.fulfillWithValue(response);
  }
);

export const verifySignupEmailOtpThunk = createAsyncThunk<
  TObject,
  VerifyOtpParams,
  IActionOptions
>(
  VERIFY_SIGNUP_EMAIL_OTP,
  async ({ email, otp, onSuccess, onError, navigate }, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());

    const response = await authService.verifySignupEmailOtp(
      baseUrl,
      email,
      otp
    );

    const status = response?.status || response?.statusCode;
    const isSuccess = status === 200 || status === 201;

    if (!isSuccess || response?.error) {
      const message = t("common:emailVerification.invalidOtp");
      onError?.(message);

      thunkAPI.dispatch(
        showAlert({
          message,
          type: TOAST_VARIANT.ERROR,
        })
      );

      return thunkAPI.rejectWithValue({ message });
    }
    onSuccess?.(response);

    thunkAPI.dispatch(
      showAlert({
        message: response?.message || t("common:emailVerification.otpVerified"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );
    return thunkAPI.fulfillWithValue(response);
  }
);
